import PropTypes from 'prop-types';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { DIRECTIONS, useDirection, useTranslation } from '@eti/providers';
import { Icon } from '@eti/components';
import { ALL, SOME, NONE } from '../../../constants/transportationMeansConstants';
import { getBoundTitle } from '../utils/format';

const { LTR } = DIRECTIONS;

const getDirection = (direction, isMultiStop, type) => {
  if (isMultiStop) {
    return direction !== LTR ? 'rotateY(180deg)' : 'rotateY(0deg)';
  }

  return (type !== 'inBound') !== (direction !== LTR) ? 'rotateY(0)' : 'rotateY(180deg)';
};

const headerStyles = css`
  align-content: center;
  background-color: ${etiColors.grayScale[4]};
  color: ${etiColors.grayScale[10]};
  display: flex;
  font-size: 0.75rem;
  padding: 4px 10px;

  @media ${mediaQueries.large.up} {
    font-size: 0.875rem;
  }
`;

const textStyles = (direction) => css`
  display: flex;
  font-weight: 700;
  ${direction === LTR ? 'margin-left: 0.3rem;' : 'margin-right: 0.3rem;'};
`;

const innerTextStyles = css`
  text-transform: uppercase;
`;

const dateBlockStyles = css`
  font-weight: 400;
  margin-left: 0.5rem;

  [dir='rtl'] & {
    margin-left: 0;
    margin-right: 0.5rem;
  }
`;

const plusIconStyles = (direction) => css`
  color: ${etiColors.grayScale[10]};
  font-size: 1rem;
  line-height: 1rem;
  ${direction === LTR ? 'margin-right: 0.2rem;' : 'margin-left: 0.2rem;'};
`;

const iconClassName = (direction, isMultiStop, type) => css`
  margin-top: 2px;
  ${direction === LTR ? 'margin-right: 0.2rem;' : 'margin-left: 0.2rem;'};
  transform: ${getDirection(direction, isMultiStop, type)};
`;

const TripDetailsBoundHeader = ({
  departureAt,
  isMultiStop,
  includesTrainSegments,
  boundNumber,
  type,
}) => {
  const { direction } = useDirection();
  const { t } = useTranslation();
  const title = t(getBoundTitle(type, isMultiStop), boundNumber);

  return (
    <header className={headerStyles}>
      {includesTrainSegments === ALL && (
        <Icon
          className={iconClassName(direction, isMultiStop, type)}
          color={etiColors.grayScale[10]}
          data-testid="tripDetails-bound-train-icon"
          id="train"
          size="0.9rem"
        />
      )}
      {includesTrainSegments === SOME && (
        <>
          <Icon
            className={iconClassName(direction, isMultiStop, type)}
            color={etiColors.grayScale[10]}
            data-testid="tripDetails-bound-plane-icon"
            id="plane-bold"
            size="0.9rem"
          />
          <span className={plusIconStyles(direction)}> + </span>
          <Icon
            className={iconClassName(direction, isMultiStop, type)}
            color={etiColors.grayScale[10]}
            data-testid="tripDetails-bound-train-icon"
            id="train"
            size="0.9rem"
          />
        </>
      )}
      {includesTrainSegments === NONE && (
        <Icon
          className={iconClassName(direction, isMultiStop, type)}
          color={etiColors.grayScale[10]}
          data-testid="tripDetails-bound-plane-icon"
          id="plane-bold"
          size="0.9rem"
        />
      )}
      <h2 className={textStyles(direction)}>
        <div className={innerTextStyles}>{title}</div>
        <div className={dateBlockStyles} data-testid="tripDetails-boundHeader-date">
          {departureAt}
        </div>
      </h2>
    </header>
  );
};

TripDetailsBoundHeader.propTypes = {
  boundNumber: PropTypes.number,
  departureAt: PropTypes.string,
  includesTrainSegments: PropTypes.oneOf([ALL, SOME, NONE]),
  isMultiStop: PropTypes.bool,
  type: PropTypes.string,
};

export default TripDetailsBoundHeader;
